/**
 * Hand-rolled shadcn-style Card primitive — no shadcn CLI dependency.
 * shadcn's philosophy is "copy the source, own the code"; we just skip the
 * intermediate copy step.
 *
 * The Card is the building block for every tile on the scorecard. Subcomponents
 * (Header, Title, Description, Content, Footer) are exported as plain `<div>`s
 * with Tailwind class compositions so consumers can override via `className`.
 *
 * Carried forward verbatim from packages/spa/src/components/ui/Card.tsx.
 */
import { type HTMLAttributes, type ReactNode } from 'react';

function cn(...classes: (string | false | undefined | null)[]): string {
  return classes.filter(Boolean).join(' ');
}

type DivProps = HTMLAttributes<HTMLDivElement> & { children?: ReactNode };

export function Card({ className, children, ...rest }: DivProps): React.JSX.Element {
  return (
    <div
      className={cn(
        // Lighter chrome (more data-ink): subtler border + shadow, 14px radius.
        // Full-height flex column so the card stretches to fill equal-height
        // grid rows (items-stretch) and the filler zone can pin to the bottom.
        'rounded-[14px] border border-ink-100 bg-white shadow-[0_1px_2px_rgba(16,28,45,0.04)]',
        'flex h-full flex-col',
        'p-4',
        className,
      )}
      {...rest}
    >
      {children}
    </div>
  );
}

export function CardHeader({ className, children, ...rest }: DivProps): React.JSX.Element {
  return (
    <div className={cn('mb-3 flex items-start justify-between gap-2', className)} {...rest}>
      {children}
    </div>
  );
}

export function CardTitle({ className, children, ...rest }: DivProps): React.JSX.Element {
  return (
    <h3
      className={cn(
        'text-[0.72rem] font-semibold uppercase tracking-wider text-ink-500',
        className,
      )}
      {...rest}
    >
      {children}
    </h3>
  );
}

export function CardDescription({
  className,
  children,
  ...rest
}: DivProps): React.JSX.Element {
  return (
    <p className={cn('text-sm text-ink-500', className)} {...rest}>
      {children}
    </p>
  );
}

export function CardContent({ className, children, ...rest }: DivProps): React.JSX.Element {
  return (
    <div className={cn('text-ink-900', className)} {...rest}>
      {children}
    </div>
  );
}

export function CardFooter({ className, children, ...rest }: DivProps): React.JSX.Element {
  return (
    <div className={cn('mt-4 text-sm text-ink-500', className)} {...rest}>
      {children}
    </div>
  );
}
